import { useCallback, useEffect, useMemo, useState } from 'react';
import { BsArrowUp, BsCalendar3, BsFacebook, BsGear, BsInfoCircle, BsInstagram } from 'react-icons/bs';
import { Link, useNavigate } from 'react-router-dom';
import caphData from '../datas/capharnaumPage.json';
import CapharnaumIntro from '../components/CapharnaumIntro';
import PlanningAgenda from '../components/PlanningAgenda';
import '../styles/Capharnaum.css';

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || 'https://directus.saladesupreme.tarrieu.fr';
const DIRECTUS_EVENTS_URL = `${DIRECTUS_URL}/items/caphEvent?fields=id,title,description,startDate,endDate,canceled,link,tarifNormal,tarifSoutien,tarifSolidaire,eventType.id,eventType.name,eventType.description,eventType.backgroundColor,eventType.borderColor,eventType.textColor,intervenant.id,intervenant.name,intervenant.instagram,intervenant.bio&sort=startDate`;
const ADMIN_TOKEN_STORAGE_KEY = 'salade-supreme-directus-token';
const SOCIAL_PAGES = {
    instagram: {
        name: 'Instagram',
        label: '@capharnaum_cafe',
        url: 'https://www.instagram.com/capharnaum_cafe/'
    },
    facebook: {
        name: 'Facebook',
        label: '@collectifsaladesupreme',
        url: 'https://www.facebook.com/collectifsaladesupreme'
    }
};

function getEventDate(event) {
    return String(event.startDate || '').slice(0, 10);
}

function getActivityKind(activity) {
    const type = String(activity.type || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

    if (type.includes('couture')) {
        return 'cours de couture';
    }

    if (type.includes('atelier')) {
        return 'atelier créatif';
    }

    if (type.includes('stage')) {
        return 'stage';
    }

    return null;
}

function isActivityCanceled(activity) {
    return activity.canceled === true;
}

function getEventTypeLabel(event) {
    const eventType = String(event.eventType?.name || '').trim();
    const normalizedType = eventType.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    if (normalizedType.includes('atelier')) {
        return 'Atelier Créatif';
    }

    if (normalizedType.includes('stage')) {
        return 'Stages';
    }

    if (normalizedType.includes('couture')) {
        return 'Cours de couture';
    }

    return eventType;
}

function formatActivityDate(dateValue) {
    const formattedDate = new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    }).format(new Date(`${dateValue}T00:00:00`));

    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
}

function normalizeDirectusEvents(events = []) {
    const monthMap = new Map();
    (Array.isArray(events) ? events : []).forEach((event, eventIndex) => {
        const eventDate = getEventDate(event);
        const endDate = String(event.endDate || '').slice(0, 10);
        const activity = {
            id: event.id,
            date: eventDate,
            endDate,
            time: String(event.startDate || '').slice(11, 16),
            endTime: String(event.endDate || '').slice(11, 16),
            title: event.title,
            description: event.description,
            type: getEventTypeLabel(event),
            typeDescription: event.eventType?.description,
            typeStyle: {
                background: event.eventType?.backgroundColor,
                border: event.eventType?.borderColor,
                text: event.eventType?.textColor
            },
            canceled: event.canceled,
            link: event.link,
            tarifNormal: event.tarifNormal,
            tarifSoutien: event.tarifSoutien,
            tarifSolidaire: event.tarifSolidaire,
            intervenant: event.intervenant && typeof event.intervenant === 'object' ? event.intervenant : null
        };

        if (!eventDate) {
            const monthId = `month-${eventIndex}`;
            monthMap.set(monthId, { id: monthId, name: monthId, activities: [activity] });
            return;
        }

        const firstMonth = new Date(`${eventDate}T00:00:00`);
        const lastMonth = new Date(`${(endDate >= eventDate ? endDate : eventDate)}T00:00:00`);
        const monthCursor = new Date(firstMonth.getFullYear(), firstMonth.getMonth(), 1);
        const lastMonthIndex = lastMonth.getFullYear() * 12 + lastMonth.getMonth();

        while (monthCursor.getFullYear() * 12 + monthCursor.getMonth() <= lastMonthIndex) {
            const monthId = `${monthCursor.getFullYear()}-${String(monthCursor.getMonth() + 1).padStart(2, '0')}`;
            if (!monthMap.has(monthId)) {
                monthMap.set(monthId, {
                    id: monthId,
                    name: new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(monthCursor),
                    activities: []
                });
            }

            monthMap.get(monthId).activities.push(activity);
            monthCursor.setMonth(monthCursor.getMonth() + 1);
        }
    });

    return Array.from(monthMap.values()).sort((first, second) => first.id.localeCompare(second.id));
}

function Capharnaum() {
    const navigate = useNavigate();
    const caph = caphData;
    const [planningMonths, setPlanningMonths] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [socialDialog, setSocialDialog] = useState(null);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [adminToken, setAdminToken] = useState(() => window.sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '');
    const handleSelectedActivityChange = (activity) => setSelectedActivity(activity);
    const handleExpiredAdminSession = useCallback(() => {
        window.sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        setAdminToken('');
        navigate('/admin', { replace: true });
    }, [navigate]);
    const refreshPlanningAfterEdit = async () => {
        const eventsResponse = await fetch(DIRECTUS_EVENTS_URL);
        if (!eventsResponse.ok) {
            throw new Error(`Événement enregistré, mais le planning n’a pas pu être actualisé (${eventsResponse.status}).`);
        }
        const eventsPayload = await eventsResponse.json();
        setPlanningMonths(normalizeDirectusEvents(eventsPayload.data));
        setSelectedActivity(null);
    };
    const upcomingActivities = useMemo(() => {
        const today = new Date();
        const todayDateKey = [
            today.getFullYear(),
            String(today.getMonth() + 1).padStart(2, '0'),
            String(today.getDate()).padStart(2, '0')
        ].join('-');
        const eligibleActivities = planningMonths
            .flatMap((month) => month.activities)
            .filter((activity) => getActivityKind(activity) && !isActivityCanceled(activity) && activity.date >= todayDateKey);
        const uniqueActivities = Array.from(new Map(
            eligibleActivities.map((activity) => [activity.id ?? activity, activity])
        ).values());
        uniqueActivities.sort((first, second) => first.date.localeCompare(second.date) || String(first.time || '').localeCompare(String(second.time || ''), 'fr', { numeric: true }));
        const nextDate = uniqueActivities[0]?.date;

        return nextDate ? uniqueActivities.filter((activity) => activity.date === nextDate) : [];
    }, [planningMonths]);
    const activeSocialPage = socialDialog ? SOCIAL_PAGES[socialDialog] : null;

    useEffect(() => {
        let isMounted = true;

        const loadPlanningData = async () => {
            try {
                const eventsResponse = await fetch(DIRECTUS_EVENTS_URL);

                if (!eventsResponse.ok) {
                    throw new Error(`Unexpected response from Directus (${eventsResponse.status})`);
                }

                const eventsPayload = await eventsResponse.json();
                if (!Array.isArray(eventsPayload.data)) {
                    throw new Error('Unexpected Directus response: expected a data array');
                }

                if (!isMounted) {
                    return;
                }

                setApiError(false);
                setPlanningMonths(normalizeDirectusEvents(eventsPayload.data));
                setIsLoading(false);
            } catch (error) {
                console.error('Unable to fetch Capharnaüm events from Directus.', error);

                if (!isMounted) {
                    return;
                }

                setApiError(true);
                setIsLoading(false);
            }
        };

        loadPlanningData();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!socialDialog) {
            return undefined;
        }

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setSocialDialog(null);
            }
        };

        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [socialDialog]);

    return (
        <section className={`capharnaum-page ${!apiError && upcomingActivities.length ? 'capharnaum-page--with-upcoming' : ''}`}>
            <nav className="capharnaum-quick-navigation" aria-label="Navigation rapide dans le Capharnaüm">
                <button
                    type="button"
                    aria-label="Remonter en haut de page"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <BsArrowUp aria-hidden="true" />
                    <span className="capharnaum-quick-navigation__label" aria-hidden="true">Haut de page</span>
                </button><button
                    type="button"
                    aria-label="Aller aux informations"
                    onClick={() => document.getElementById('capharnaum-info')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                    <BsInfoCircle aria-hidden="true" />
                    <span className="capharnaum-quick-navigation__label" aria-hidden="true">Infos pratiques</span>
                </button>
                <button
                    type="button"
                    aria-label="Aller au calendrier"
                    onClick={() => document.getElementById('cours-ateliers')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                    <BsCalendar3 aria-hidden="true" />
                    <span className="capharnaum-quick-navigation__label" aria-hidden="true">Calendrier</span>
                </button>
                {!adminToken && (
                    <>
                        <button
                            type="button"
                            aria-label="Instagram du Capharnaüm"
                            onClick={() => setSocialDialog('instagram')}
                        >
                            <BsInstagram aria-hidden="true" />
                            <span className="capharnaum-quick-navigation__label" aria-hidden="true">Instagram du Capharnaüm</span>
                        </button>
                        <button
                            type="button"
                            aria-label="Facebook du collectif Salade Suprême"
                            onClick={() => setSocialDialog('facebook')}
                        >
                            <BsFacebook aria-hidden="true" />
                            <span className="capharnaum-quick-navigation__label" aria-hidden="true">Facebook du collectif</span>
                        </button>
                    </>
                )}
                {adminToken && (
                    <Link to="/admin" aria-label="Accéder à l’administration">
                        <BsGear aria-hidden="true" />
                        <span className="capharnaum-quick-navigation__label" aria-hidden="true">Administration</span>
                    </Link>
                )}
            </nav>
            {activeSocialPage && (
                <div className="capharnaum-social-dialog-overlay" onClick={() => setSocialDialog(null)}>
                    <section
                        className="capharnaum-social-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="capharnaum-social-dialog-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h2 id="capharnaum-social-dialog-title">{activeSocialPage.name}</h2>
                        <p>Retrouvez plus d’informations sur les événements organisés au café sur la page {activeSocialPage.name} du Capharnaüm.</p>
                        <div className="capharnaum-social-dialog__actions">
                            <button type="button" onClick={() => setSocialDialog(null)}>Annuler</button>
                            <a href={activeSocialPage.url} target="_blank" rel="noreferrer noopener">{activeSocialPage.label}</a>
                        </div>
                    </section>
                </div>
            )}
            {!apiError && upcomingActivities.length > 0 && (
                <>
                <button
                    type="button"
                    className="capharnaum-upcoming-banner"
                    style={{
                        '--capharnaum-upcoming-background': upcomingActivities[0].typeStyle?.background || '#F8C7CD',
                        '--capharnaum-upcoming-border': upcomingActivities[0].typeStyle?.border || '#111',
                        '--capharnaum-upcoming-text': upcomingActivities[0].typeStyle?.text || '#111'
                    }}
                    aria-label={`Afficher le planning : ${upcomingActivities.map((activity) => `${activity.title}, ${formatActivityDate(activity.date)}${activity.time ? ` à ${activity.time}` : ''}`).join(' ; ')}`}
                    aria-describedby="capharnaum-upcoming-tooltip"
                    onClick={(event) => {
                        const activityIndex = Number(event.target.closest('[data-upcoming-activity-index]')?.dataset.upcomingActivityIndex);
                        setSelectedActivity(upcomingActivities[activityIndex] || upcomingActivities[0]);
                        window.requestAnimationFrame(() => document.getElementById('cours-ateliers')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                    }}
                >
                    <span className="capharnaum-upcoming-banner__entrance" aria-hidden="true">
                        <span className="capharnaum-upcoming-banner__track">
                            {Array.from({ length: 16 }, (_, copy) => upcomingActivities.map((activity, index) => (
                                <span
                                    className="capharnaum-upcoming-banner__message"
                                    key={`${copy}-${activity.id ?? index}`}
                                    data-upcoming-activity-index={index}
                                    style={{
                                        '--capharnaum-upcoming-background': activity.typeStyle?.background || '#F8C7CD',
                                        '--capharnaum-upcoming-border': activity.typeStyle?.border || '#111',
                                        '--capharnaum-upcoming-text': activity.typeStyle?.text || '#111'
                                    }}
                                >
                                    Prochain {getActivityKind(activity)}
                                    <span className="capharnaum-upcoming-banner__separator">·</span>
                                    {formatActivityDate(activity.date)}
                                    {activity.time && (
                                        <>
                                            <span className="capharnaum-upcoming-banner__separator">·</span>
                                            {activity.time}
                                        </>
                                    )}
                                    <span className="capharnaum-upcoming-banner__separator">·</span>
                                    {activity.title}
                                    <span className="capharnaum-upcoming-banner__separator">·</span>
                                </span>
                            )))}
                        </span>
                    </span>
                </button>
                <span id="capharnaum-upcoming-tooltip" className="capharnaum-upcoming-tooltip" role="tooltip">
                    Cliquer sur un événement pour le retrouver dans le calendrier.
                </span>
                </>
            )}
            <CapharnaumIntro
                title={caph.title}
                theme={caph.theme}
                description={caph.description}
                bannerImg={caph.bannerImg}
                bannerImgM={caph.bannerImgM}
                altBannerImg={caph.altBannerImg}
                activities={caph.activities}
                atelierLabel={caph.atelierLabel}
                atelierUrl={caph.atelierUrl}
                hours={caph.hours}
                location={caph.location}
                img1={caph.img1}
                img2={caph.img2}
                img3={caph.img3}
                img4={caph.img4}
                alt1={caph.alt1}
                alt2={caph.alt2}
                alt3={caph.alt3}
                alt4={caph.alt4}
            />
            <PlanningAgenda
                months={planningMonths}
                eventLocation={`${caph.location.address}, Toulouse, France`}
                isLoading={isLoading}
                errorMessage={apiError ? 'Le calendrier est temporairement indisponible. Merci de réessayer plus tard.' : null}
                selectedActivity={selectedActivity}
                onSelectedActivityChange={handleSelectedActivityChange}
                isAdmin={Boolean(adminToken)}
                adminToken={adminToken}
                onActivitySaved={refreshPlanningAfterEdit}
                onUnauthorized={handleExpiredAdminSession}
                activityDescriptions={[
                    {
                        type: 'Atelier Créatif',
                        title: 'Ateliers créatifs',
                        description: 'Des rendez-vous pour explorer une pratique artistique, expérimenter et réaliser une création.'
                    },
                    {
                        type: 'Stages',
                        title: 'Stages',
                        description: 'Des temps dédiés pour approfondir une pratique et développer un projet créatif.'
                    },
                    {
                        type: 'Cours de couture',
                        title: 'Cours de couture',
                        description: 'Des cours de couture pour apprendre et pratiquer la confection et la création textile.'
                    }
                ]}
            />
        </section>
    );
}

export default Capharnaum;
